import React from 'react';
import styled from 'styled-components';
import { navigate } from 'gatsby';
import { FormattedMessage, useIntl } from 'react-intl';

import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';

import { Container, Section, FeatureTitle } from '../global';

const StyledSection = styled(Section)`
  background-color: ${(props) => props.theme.color.background.light};
`;

const PricingContainer = styled(Container)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: 50px 0 0px;
`;

const GetStartedTitle = styled.h3`
  margin: 0 auto 32px;
  text-align: center;
`;

const PricingCardTitle = styled.h2`
  margin: 0 auto 20px;
  text-align: center;
  font-family: 'HK Grotesk Black';
`;
const PricingCardSubTitle = styled.span`
  margin: 0 auto;
  text-align: center;
  font-family: 'HK Grotesk Black';
`;

const HeaderTextGroup = styled.div`
  margin: 0;
  margin-top: 48px;

  h2 {
    margin-bottom: 24px;
    ${(props) => props.theme.font_size.regular}
  }
`;

const PricingTitleV2 = styled(FeatureTitle)`
  && {
    width: 100%;
    background-color: ${(props) => props.theme.color.secondary};
    padding: 10px 0px;
    color: ${(props) => props.theme.color.white.regular};
    margin: 10px 0px;
  }
`;

const Table = styled.table`
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
`;

const Th = styled.th`
  display: none;
  height: 53px;
  empty-cells: show;
  text-align: center;
  @media (min-width: ${(props) => props.theme.screen.sm}) {
    display: table-cell !important;
    width: 330px;
  }
`;

const ThPricing = styled.th`
  display: none;
  height: 53px;
  empty-cells: show;
  text-align: center;
  @media (min-width: ${(props) => props.theme.screen.sm}) {
    display: table-cell !important;
    width: 330px;
  }
  background-color: ${(props) => props.theme.color.secondary};
  border-style: solid;
  border-color: white;
`;

const Td = styled.td`
  height: 53px;
  padding: 10px;
  empty-cells: show;
  text-align: center;
  @media (min-width: ${(props) => props.theme.screen.sm}) {
    display: table-cell !important;
    width: 330px;
  }
`;

const EmptyTd = styled(Td)`
  border: 0;
  background: null;
`;

const ColHeader = styled(Td)`
  text-align: center;
  font-weight: bold;
  padding: 0 10px;
`;

const GreenCheckIcon = styled(CheckIcon)`
  color: #2ca01c;
`;

const RedClearIcon = styled(ClearIcon)`
  color: #f44336;
`;

const Tr = styled.tr`
  background: ${(props) => (props.index % 2 === 0 ? '' : '#ddd')};
`;

const H2Left = styled.h2`
  font-size: 18px;
  text-align: left;
`;

const SignupContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const TryItButtonV2 = styled.button`
  && {
    font-size: 14px;
    color: white;
    letter-spacing: 1px;
    height: 60px;
    display: block;
    margin: 0px;
    text-transform: uppercase;
    cursor: pointer;
    white-space: nowrap;
    background: ${(props) => props.theme.color.accent};
    border-radius: 4px;
    padding: 0px 40px;
    border-width: 0px;
    border-style: initial;
    border-color: initial;
    border-image: initial;
    font-family: ${(props) => props.theme.font.extrabold};
    font-weight: ${(props) => props.theme.font_weight.extrabold};
    outline: 0px;
    &:hover {
      box-shadow: rgba(110, 120, 152, 0.22) 0px 2px 10px 0px;
    }
    @media (max-width: ${(props) => props.theme.screen.md}) {
      height: 40px;
      font-size: 12px;
      padding: 0px 20px;
    }
  }
`;

const getCellContent = (val) =>
  (val === true && <GreenCheckIcon />) || val || <RedClearIcon />;

const getCellHeader = (header, val) =>
  (val === true && header) ||
  (val && <PricingCardTitle>{header}</PricingCardTitle>) ||
  header;

const Pricing = () => {
  const theme = useTheme();
  const smallDevice = useMediaQuery(theme.breakpoints.down('sm'));

  const { formatMessage: f, locale } = useIntl();

  const features = [
    [f({ id: 'pricingFeature1' }), false, true],
    [f({ id: 'pricingFeature2' }), false, true],
    [f({ id: 'pricingFeature3' }), false, true],
    [f({ id: 'pricingFeature4' }), false, true],
    [f({ id: 'pricingFeature5' }), false, true],
    [f({ id: 'pricingFeature6' }), false, true],
    [f({ id: 'pricingFeature7' }), false, true],
    [f({ id: 'pricingFeature8' }), true, true],
    [f({ id: 'pricingFeature9' }), true, true],
    [f({ id: 'pricingFeature10' }), true, true],
    [f({ id: 'pricingFeature11' }), true, true],
    [f({ id: 'pricingFeature12' }), true, true],
  ];
  const pricingPackages = [
    f({ id: 'pricingOption1' }),
    f({ id: 'pricingOption2' }),
    f({ id: 'pricingOption3' }),
    f({ id: 'pricingOption4' }),
    f({ id: 'pricingOption5' }),
  ];

  const pricingMap = {
    hu: ['7.490 Ft', '10.490 Ft', '12.490 Ft', '15.990 Ft', '20.990 Ft'],
    en: ['£40', '£50', '£60', '£70', '£80'],
  };

  return (
    <StyledSection id="packages">
      <PricingContainer>
        {/* <GetStartedTitle>
          <FormattedMessage id="pricingPackages" />
        </GetStartedTitle>
        <Table>
          <thead>
            <tr>
              <EmptyTd />
              {smallDevice ? (
                <>
                  <Td>
                    <ColHeader>
                      <FormattedMessage id="claimPricingCol2" />
                    </ColHeader>
                  </Td>
                  <Td>
                    <ColHeader>
                      <FormattedMessage id="claimPricingCol3" />
                    </ColHeader>
                  </Td>
                </>
              ) : (
                <>
                  <ThPricing>
                    <PricingTitleV2>
                      <FormattedMessage id="claimPricingCol2" />
                    </PricingTitleV2>
                  </ThPricing>
                  <ThPricing>
                    <PricingTitleV2>
                      <FormattedMessage id="claimPricingCol3" />
                    </PricingTitleV2>
                  </ThPricing>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {features.map((x, i) => (
              <Tr index={i} key={`pricing-${i}`}>
                <ColHeader>{getCellHeader(x[0], x[1])}</ColHeader>
                <Td>{getCellContent(x[1])}</Td>
                <Td>{getCellContent(x[2])}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
        <div style={{ marginTop: '40px' }} /> */}
        <GetStartedTitle>{f({ id: 'pricingSubtitle' })}</GetStartedTitle>
        <div>
          <Table>
            <thead>
              {smallDevice ? (
                <tr>
                  <Td>
                    <ColHeader>
                      <FormattedMessage id="claimPricingCol1" />
                    </ColHeader>
                  </Td>
                  <Td>
                    <ColHeader>
                      <FormattedMessage id="claimPricingCol3" />
                    </ColHeader>
                  </Td>
                </tr>
              ) : (
                <tr>
                  <ThPricing>
                    <PricingTitleV2>
                      {f({ id: 'claimPricingCol1' })}
                    </PricingTitleV2>
                  </ThPricing>
                  <ThPricing>
                    <PricingTitleV2>
                      {f({ id: 'claimPricingCol3' })}
                    </PricingTitleV2>
                  </ThPricing>
                </tr>
              )}
            </thead>
            <tbody>
              {pricingMap[locale]?.map((x, i) => (
                <Tr index={i} key={x}>
                  <ColHeader>{pricingPackages[i]}</ColHeader>
                  <Td>{x}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
      </PricingContainer>
      <Container>
        <HeaderTextGroup>
          <FormattedMessage
            id="pricingParagraph1"
            values={{
              h2: (...chunks) => <h2>{chunks}</h2>,
              p: (...chunks) => <p>{chunks}</p>,
            }}
          />
        </HeaderTextGroup>
      </Container>
    </StyledSection>
  );
};

export default Pricing;
